/**
 * Settings > My-Kitchen page (Plan 19).
 *
 * CHEF_ADMIN / CHEF_STAFF configures the kitchen's structural choices.
 * Four sections (per the plan's Scope):
 *
 *   1. Vendor profile — self-service fields + Ordo-provisioned reads
 *      with Request-a-change CTAs.
 *   2. Packaging configs — deferred to apps/menu-builder-demo (Bundle /
 *      BundleDeliverable rows replaced the legacy PackagingConfig model);
 *      we render a stub card that explains where authoring happens +
 *      offers a Request-a-change CTA.
 *   3. Team roster — CHEF_ADMIN-only. CHEF_STAFF sees the page without
 *      this section per Acceptance Criterion 9.
 *   4. Payouts (Tipalti placeholder) — read-only payout-destination
 *      summary + a single Request-a-payout-change CTA.
 *
 * Multi-kitchen edge: a chef with admin access at >1 kitchen org sees a
 * picker; v1 just renders the first kitchen if no selection.
 */

import { Badge } from '@ordo/ui/badge'
import { EmptyState } from '@ordo/ui/empty-state'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Skeleton } from '@ordo/ui/skeleton'
import { ChefHat } from 'lucide-react'
import { useMemo, useState } from 'react'

import { type KitchenOrganizationSummary, useKitchenDetailQuery, useMyKitchensQuery } from '@/api/kitchen-settings'
import { ChefOnboardingDocumentsCard } from '@/components/chefs/onboarding-documents-card'
import { useProfile } from '@/stores/profile'

import { KitchenChangeRequestModal } from '../../components/settings/kitchen/kitchen-change-request-modal'
import { MeasurementDisplayCard } from '../../components/settings/kitchen/measurement-display-card'
import { PackagingConfigsEditor } from '../../components/settings/kitchen/packaging-configs-editor'
import { TeamRosterTable } from '../../components/settings/kitchen/team-roster-table'
import { TipaltiCard } from '../../components/settings/kitchen/tipalti-card'
import { VendorProfileCard } from '../../components/settings/kitchen/vendor-profile-card'

export function MyKitchenSection() {
  const { profile } = useProfile()
  const kitchensQuery = useMyKitchensQuery()
  const kitchens = useMemo<KitchenOrganizationSummary[]>(
    () => kitchensQuery.data?.me?.kitchenOrganizations ?? [],
    [kitchensQuery.data],
  )
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

  const isAdminAtSelected = useMemo(() => {
    const id = selectedOrgId ?? kitchens[0]?.id
    if (!id) {
      return false
    }
    const k = kitchens.find((x) => x.id === id)
    return !!k?.profileCodes.some((code) => code === 'CHEF_ADMIN' || code === 'SUPPLIER_ADMIN')
  }, [kitchens, selectedOrgId])

  const kitchen = useMemo<KitchenOrganizationSummary | null>(() => {
    const id = selectedOrgId ?? kitchens[0]?.id ?? null
    if (!id) {
      return null
    }
    return kitchens.find((k) => k.id === id) ?? null
  }, [kitchens, selectedOrgId])

  if (kitchensQuery.isPending && !kitchensQuery.data) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (kitchensQuery.error) {
    return <ErrorPanel message={kitchensQuery.error.message} />
  }
  if (kitchens.length === 0) {
    return (
      <EmptyState
        icon={<ChefHat className="h-12 w-12" />}
        title="No kitchen memberships"
        description="Kitchen settings show up here once you're added as an admin or staff to a kitchen organization."
      />
    )
  }
  if (!kitchen) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-medium">
          {kitchen.profileCodes.some((code) => code === 'SUPPLIER_ADMIN' || code === 'SUPPLIER_STAFF')
            ? 'My Supplier Business'
            : 'My Kitchen'}
        </h2>
        {kitchens.length > 1 ? (
          <KitchenPicker kitchens={kitchens} value={kitchen.id} onChange={setSelectedOrgId} />
        ) : (
          <Badge variant="secondary">{kitchen.name}</Badge>
        )}
      </div>

      <KitchenDetailSection
        supplier={kitchen.profileCodes.some((code) => code === 'SUPPLIER_ADMIN' || code === 'SUPPLIER_STAFF')}
        kitchenOrgId={kitchen.id}
        actorUserId={profile?.user?.id ?? null}
        isAdmin={isAdminAtSelected}
        canUploadDocuments={kitchen.profileCodes.includes('CHEF_ADMIN')}
      />
    </div>
  )
}

function KitchenDetailSection({
  supplier,
  kitchenOrgId,
  actorUserId,
  isAdmin,
  canUploadDocuments,
}: {
  supplier: boolean
  kitchenOrgId: string
  actorUserId: string | null
  isAdmin: boolean
  canUploadDocuments: boolean
}) {
  const detailQuery = useKitchenDetailQuery(kitchenOrgId)

  if (detailQuery.isPending && !detailQuery.data) {
    return <Skeleton className="h-64 w-full" />
  }
  if (detailQuery.error) {
    return <ErrorPanel message={detailQuery.error.message} />
  }
  if (!detailQuery.data) {
    return null
  }
  const organization = detailQuery.data.organization

  return (
    <div className="flex flex-col gap-4">
      <VendorProfileCard organization={organization} />
      {canUploadDocuments ? <ChefOnboardingDocumentsCard organizationId={kitchenOrgId} /> : null}
      <MeasurementDisplayCard kitchenOrgId={kitchenOrgId} isAdmin={isAdmin} />
      <PackagingConfigsEditor kitchenOrgId={kitchenOrgId} />
      {isAdmin && actorUserId ? (
        <TeamRosterTable supplier={supplier} kitchenOrgId={kitchenOrgId} actorUserId={actorUserId} />
      ) : isAdmin ? (
        // CHEF_ADMIN but profile hasn't loaded yet — render a skeleton.
        <Skeleton className="h-40 w-full" />
      ) : null}
      <TipaltiCard kitchenOrgId={kitchenOrgId} />
    </div>
  )
}

function KitchenPicker({
  kitchens,
  value,
  onChange,
}: {
  kitchens: KitchenOrganizationSummary[]
  value: string
  onChange: (id: string) => void
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-72">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {kitchens.map((k) => (
          <SelectItem key={k.id} value={k.id}>
            {k.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
      Couldn't load kitchen settings. {message}
    </div>
  )
}

// Re-export the change-request modal so any external surface that wants
// the same flow can import it from one place (mirrors the school-side
// `RequestChangeModal` export from `@/components/settings/request-change-modal`).
export { KitchenChangeRequestModal }
